/** Hermes 品牌站智能体可读性优化场景：context / 复用键。 */

export type BrandSiteScoreOptimizeContext = {
  mode: "brand_site_score_optimize";
  accountId: string;
  brandId: number;
  brandSiteId: number;
};

export function brandSiteScoreOptimizeChatContext(ctx: BrandSiteScoreOptimizeContext) {
  return {
    mode: "brand_site_score_optimize" as const,
    brand_id: ctx.brandId,
    brand_site_id: ctx.brandSiteId,
    materials_bindings: [
      { scope: "enterprise" as const, access: "read" as const },
      { scope: "brand" as const, brand_id: ctx.brandId, access: "read" as const },
    ],
  };
}

export function shouldReplaceBrandSiteScoreOptimize(
  current: Pick<BrandSiteScoreOptimizeContext, "brandSiteId"> | null | undefined,
  nextSiteId: number,
): boolean {
  if (!current) return false;
  return Number(current.brandSiteId) !== Number(nextSiteId);
}

/** 异步启动返回时，仅允许仍有效的同站点请求落入 Provider 状态。 */
export function shouldAcceptBrandSiteScoreOptimizeStart(
  active: boolean,
  current: Pick<BrandSiteScoreOptimizeContext, "brandSiteId"> | null | undefined,
  currentGeneration: number,
  requestedGeneration: number,
  requestedSiteId: number,
): boolean {
  return (
    active &&
    currentGeneration === requestedGeneration &&
    Number(current?.brandSiteId) === Number(requestedSiteId)
  );
}

/** 异步草稿预评返回时，目标站点与请求代次都必须仍然有效。 */
export function shouldAcceptBrandSiteDraftPreview(
  currentSiteId: number,
  currentGeneration: number,
  requestedSiteId: number,
  requestedGeneration: number,
): boolean {
  return (
    Number(currentSiteId) === Number(requestedSiteId) &&
    currentGeneration === requestedGeneration
  );
}

/** 尚无评分，或正式分 / 草稿分任一低于 90 时，展示优化入口。 */
export function showBrandSiteScoreOptimizeEntry(
  siteScore: number | null | undefined,
  draftScore: number | null | undefined,
): boolean {
  const scores = [siteScore, draftScore].filter((score): score is number => score != null);
  if (scores.length === 0) return true;
  return scores.some((score) => score < 90);
}

/** 打开可读性优化时预填输入框：小白只需按回车，不必自己想怎么说。 */
export function brandSiteScoreOptimizeSeedPrompt(input: {
  siteTitle?: string | null;
  siteScore?: number | null;
  siteGrade?: string | null;
  draftScore?: number | null;
  draftGrade?: string | null;
}): string {
  const scoreBits: string[] = [];
  if (input.siteGrade && input.siteScore != null) {
    scoreBits.push(`正式站 ${input.siteGrade}（${input.siteScore}）`);
  }
  if (input.draftGrade && input.draftScore != null) {
    scoreBits.push(`草稿预评 ${input.draftGrade}（${input.draftScore}）`);
  }
  const siteLabel = input.siteTitle?.trim() || "官网";
  const scoreLine =
    scoreBits.length > 0 ? `参考分数：${scoreBits.join("；")}。` : "请先给当前草稿打个分。";
  return [
    `请提升「${siteLabel}」的智能体可读性。`,
    scoreLine,
    "请先检查草稿并给出可讨论的优化计划；我确认前不要改任何页面。",
  ].join("");
}

export function matchesBrandSiteScoreOptimizeChat(
  chat: { context?: Record<string, unknown> | null },
  ctx: Pick<BrandSiteScoreOptimizeContext, "brandId" | "brandSiteId">,
): boolean {
  const context = chat.context;
  if (!context || typeof context !== "object") return false;
  if (context.mode !== "brand_site_score_optimize") return false;
  if (Number(context.brand_id) !== ctx.brandId) return false;
  return Number(context.brand_site_id) === ctx.brandSiteId;
}

export function isBrandSiteScoreOptimizeChat(chat: {
  context?: Record<string, unknown> | null;
}): boolean {
  return chat.context?.mode === "brand_site_score_optimize";
}

export function brandSiteScoreOptimizeFromChat(
  chat: { context?: Record<string, unknown> | null },
  accountId: string,
): BrandSiteScoreOptimizeContext | null {
  const context = chat.context;
  if (!context || typeof context !== "object") return null;
  if (context.mode !== "brand_site_score_optimize") return null;

  const brandId = Number(context.brand_id);
  const brandSiteId = Number(context.brand_site_id);
  if (!Number.isFinite(brandId) || brandId <= 0) return null;
  if (!Number.isFinite(brandSiteId) || brandSiteId <= 0) return null;

  return {
    mode: "brand_site_score_optimize",
    accountId,
    brandId,
    brandSiteId,
  };
}
